import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../components/CourierEntryList.css";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { hasPermission } from "../utils/permissions";
// Remote API for listing deliveries
const API_BASE = "https://newerp.awasthivitality.com/api";

const CourierEntryList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canView, setCanView] = useState(true);

  // Server-side pagination numbers
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);

  const load = async () => {
    setLoading(true);
    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = new URL(`${API_BASE}/deliveries`);
      if (token) url.searchParams.set('token', token);
      if (search) url.searchParams.set('search', search);
      url.searchParams.set('page', String(currentPage));
      url.searchParams.set('per_page', String(pageSize));
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      });
      const res = await response.json().catch(() => ({}));
      if (response.ok && res && Array.isArray(res.data)) {
        setRows(res.data);
        const total = res.pagination?.total ?? res.total ?? res.count ?? 0;
        setTotalItems(Number(total) || 0);
      } else if (Array.isArray(res)) {
        setRows(res);
        setTotalItems(res.length);
      } else {
        toast.error(res?.message || res?.msg || `Failed to load deliveries (HTTP ${response.status})`);
      }
    } catch (e) {
      toast.error("❌ Network Error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkPerms = async () => {
      const view = await hasPermission('delivery', 'viewdelivery');
      const add = await hasPermission('delivery', 'adddelivery');
      const edit = await hasPermission('delivery', 'editdelivery');
      const del = await hasPermission('delivery', 'deletedelivery');
      setCanView(!!view);
      setCanAdd(!!add);
      setCanEdit(!!edit);
      setCanDelete(!!del);
      if (!view) {
        toast.error('You do not have permission to view courier entries.');
        setTimeout(() => navigate('/DashBoard'), 2000);
        return;
      }
      load();
    };
    checkPerms();
    // Listen for permission updates
    const handlePermissionUpdate = () => {
      checkPerms();
    };
    window.addEventListener('permissionsUpdated', handlePermissionUpdate);
    return () => window.removeEventListener('permissionsUpdated', handlePermissionUpdate);
  }, [navigate]);

  // Reload when search, page, or pageSize change (debounced for search)
  useEffect(() => {
    const t = setTimeout(() => {
      if (canView) load();
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (canView) load();
  }, [page, pageSize]);

  const handleAddClick = () => navigate("/CourierEntry");
  const handleEditClick = (row) => navigate(`/CourierEntryEdit?delivery_id=${row.delivery_id}`);
  const handleDeleteClick = async (row) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = new URL(`${API_BASE}/deliveries/${row.delivery_id}`);
      if (token) url.searchParams.set('token', token);
      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      });
      const res = await response.json().catch(() => ({}));
      if (response.ok && res && res.success !== false) {
        toast.success(res.msg || "Deleted");
        setRows(prev => prev.filter(r => r.delivery_id !== row.delivery_id));
      } else {
        toast.error(res?.message || res?.msg || `Delete failed (HTTP ${response.status})`);
      }
    } catch (e) {
      toast.error("❌ Network Error. Please check your connection and try again.");
    }
  };

  if (!canView) {
    return (
      <div className="entrylist-container">
        <div className="entrylist-card">
          <div style={{ padding: 20, textAlign: 'center' }}>
            <h3>Access Denied</h3>
            <p>You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="entrylist-container">
      <div className="entrylist-card">
        <div className="entrylist-header">
          <span>Courier Entry - List {loading ? "(Loading...)" : ""}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ height: 30 }}
            />
            {canAdd && (
              <button className="entrylist-btn-add" onClick={handleAddClick}>
                <FaPlus />
              </button>
            )}
          </div>
        </div>

        <table className="entrylist-table">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Courier_Date</th>
              <th>Courier_Name</th>
              <th>Tracking_ID</th>
              <th>Consignee</th>
              <th>Consignee Mob</th>
              <th>Patient</th>
              <th>Patient_Mobile</th>
              <th>PR_No</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => (
              <tr key={item.delivery_id}>
                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                <td>{item.delivery_date}</td>
                <td>{item.courier_name}</td>
                <td>{item.tracking_no}</td>
                <td>{item.contact_name}</td>
                <td>{item.contact_mobile}</td>
                <td>{item.patient_name}</td>
                <td>{item.patient_mobile}</td>
<td
  className="entrylist-link"
  style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
  onClick={() => navigate(`/patient/details/${item.patient_id}`)}
>
  {item.pr_no}
</td>
<td>{item.status}</td>
                <td className="entrylist-actions">
                  {/* {canEdit && (
                    <button className="entrylist-btn-edit" onClick={() => handleEditClick(item)}>
                      <FaEdit />
                    </button>
                  )} */}
                  {canDelete && (
                    <button className="entrylist-btn-delete" onClick={() => handleDeleteClick(item)}>
                      <FaTrash />
                    </button>
                  )}
                  
                  <a
  href={`https://leadurl.awasthivitality.com/label-generation.php?awb=${encodeURIComponent(item.tracking_no || '')}`}
  target="_blank"
  rel="noopener noreferrer"
  style={{
    color: "blue",
    cursor: "pointer",
    textDecoration: "underline",
    marginRight: "8px"
  }}
>
  Print Label
</a>

                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No entries found</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mobile-card-list">
  {rows.map((item, index) => (
    <div key={item.delivery_id} className="mobile-card">
      <div className="mobile-card-header">
        <span className="mobile-s-no">#{(currentPage - 1) * pageSize + index + 1}</span>
        <div className="mobile-card-actions">
          {canEdit && (
            <button className="entrylist-btn-edit" onClick={() => handleEditClick(item)}>
              <FaEdit />
            </button>
          )}
          {canDelete && (
            <button className="entrylist-btn-delete" onClick={() => handleDeleteClick(item)}>
              <FaTrash />
            </button>
          )}
        </div>
      </div>
      <p><strong>Courier Date:</strong> {item.delivery_date}</p>
      <p><strong>Courier Name:</strong> {item.courier_name}</p>
      <p><strong>Tracking ID:</strong> {item.tracking_no}</p>
      <p><strong>Consignee:</strong> {item.contact_name}</p>
      <p><strong>Consignee Mob:</strong> {item.contact_mobile}</p>
      <p><strong>Patient:</strong> {item.patient_name}</p>
      <p><strong>Patient Mob:</strong> {item.patient_mobile}</p>
      <p>
        <strong>PR No:</strong>
        <span
          className="entrylist-link"
          onClick={() => navigate(`/patient/details/${item.patient_id}`)}
        >
          {item.pr_no}
        </span>
      </p>
    </div>
  ))}

  {!loading && rows.length === 0 && (
    <p style={{ textAlign: "center", padding: 16 }}>No entries found</p>
  )}
</div>


        {/* Pagination Controls */}
        <div className="entrylist-pagination">
          <div className="entrylist-page-size">
            <span>Rows per page</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>{totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems}</span>
          </div>
          <div className="entrylist-page-buttons">
            <button
              className="entrylist-page-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => {
              const pageNumber = i + 1;
              return (
                <button
                  key={pageNumber}
                  className={`entrylist-page-btn ${pageNumber === currentPage ? 'is-active' : ''}`}
                  onClick={() => setPage(pageNumber)}
                  disabled={pageNumber === currentPage}
                >
                  {pageNumber}
                </button>
              );
            })}
            {totalPages > 7 && (
              <span style={{ padding: '0 4px' }}>...</span>
            )}
            <button
              className="entrylist-page-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourierEntryList;
